import { Injectable } from '@angular/core';
import { ApiService, HandlerService } from "../../../global/services";
import { DataService } from "../../../services";
import {
  AdminCustomerSearchAnalyticsDTO,
  AdvertNotificationStatsDTO,
  AdminSearchSystemOverviewDTO,
  CustomerAnalyticsSearchDTO,
  AdvertAnalyticsSearchDTO,
  TypedSearchResultDTO,
  AdminCustomerSearchFilters,
  AdminAdvertSearchFilters
} from '../../../global/interfaces';

@Injectable()
export class AdminSearchStatsService {
  private serviceUrl = '/api/search-stats';

  constructor(
    private apiClient: ApiService,
    private data: DataService,
    private handler: HandlerService
  ) {}

  /**
   * Get system overview dashboard metrics
   */
  async getSystemOverview(): Promise<AdminSearchSystemOverviewDTO> {
    const url = `${this.data.apiUrl}${this.serviceUrl}/system-overview`;
    try {
      return await this.apiClient.get({ url }) as AdminSearchSystemOverviewDTO;
    } catch (error) {
      this.handler.handleError(error);
      throw error;
    }
  }

  /**
   * Get customer analytics with pagination and filtering
   */
  async getCustomerAnalytics(searchDTO: CustomerAnalyticsSearchDTO): Promise<TypedSearchResultDTO<AdminCustomerSearchAnalyticsDTO>> {
    const url = `${this.data.apiUrl}${this.serviceUrl}/customer-analytics`;
    try {
      return await this.apiClient.post({
        url,
        data: searchDTO,
        headers: { 'Content-Type': 'application/json' }
      }) as TypedSearchResultDTO<AdminCustomerSearchAnalyticsDTO>;
    } catch (error) {
      this.handler.handleError(error);
      throw error;
    }
  }

  /**
   * Get advert analytics with pagination and filtering
   */
  async getAdvertAnalytics(searchDTO: AdvertAnalyticsSearchDTO): Promise<TypedSearchResultDTO<AdvertNotificationStatsDTO>> {
    const url = `${this.data.apiUrl}${this.serviceUrl}/advert-analytics`;
    try {
      return await this.apiClient.post({
        url,
        data: searchDTO,
        headers: { 'Content-Type': 'application/json' }
      }) as TypedSearchResultDTO<AdvertNotificationStatsDTO>;
    } catch (error) {
      this.handler.handleError(error);
      throw error;
    }
  }

  /**
   * Export customer analytics to CSV
   */
  async exportCustomerAnalytics(searchDTO: CustomerAnalyticsSearchDTO): Promise<void> {
    const url = `${this.data.apiUrl}${this.serviceUrl}/customer-analytics`;
    try {
      // Remove pagination for export
      const exportDTO = { ...searchDTO, offset: undefined, limit: undefined };
      const response = await this.apiClient.post({
        url,
        data: exportDTO,
        headers: { 'Content-Type': 'application/json' },
        responseType: 'blob'
      }) as Blob;

      this.downloadFile(response, `customer-analytics-${this.getDateString()}.csv`);
    } catch (error) {
      this.handler.handleError(error);
      throw error;
    }
  }

  /**
   * Export advert analytics to CSV
   */
  async exportAdvertAnalytics(searchDTO: AdvertAnalyticsSearchDTO): Promise<void> {
    const url = `${this.data.apiUrl}${this.serviceUrl}/advert-analytics`;
    try {
      // Remove pagination for export
      const exportDTO = { ...searchDTO, offset: undefined, limit: undefined };
      const response = await this.apiClient.post({
        url,
        data: exportDTO,
        headers: { 'Content-Type': 'application/json' },
        responseType: 'blob'
      }) as Blob;

      this.downloadFile(response, `advert-analytics-${this.getDateString()}.csv`);
    } catch (error) {
      this.handler.handleError(error);
      throw error;
    }
  }

  /**
   * Build default customer search DTO
   */
  buildDefaultCustomerSearchDTO(overrides?: Partial<CustomerAnalyticsSearchDTO>): CustomerAnalyticsSearchDTO {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    return {
      offset: 0,
      limit: 25,
      filters: {
        fromDate: thirtyDaysAgo,
        toDate: new Date()
      },
      order: [{ column: 'notificationEffectivenessRate', descending: true }],
      ...overrides
    };
  }

  /**
   * Build default advert search DTO
   */
  buildDefaultAdvertSearchDTO(overrides?: Partial<AdvertAnalyticsSearchDTO>): AdvertAnalyticsSearchDTO {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    return {
      offset: 0,
      limit: 25,
      filters: {
        fromDate: thirtyDaysAgo,
        toDate: new Date()
      },
      order: [{ column: 'notificationEffectivenessRate', descending: true }],
      ...overrides
    };
  }

  /**
   * Get effectiveness badge class for MDBootstrap
   */
  getEffectivenessBadgeClass(rate: number): string {
    if (rate >= 70) return 'badge-success';
    if (rate >= 50) return 'badge-warning';
    if (rate >= 30) return 'badge-info';
    return 'badge-danger';
  }

  /**
   * Get effectiveness rating text
   */
  getEffectivenessRating(rate: number): string {
    if (rate >= 80) return 'Highly Effective';
    if (rate >= 60) return 'Effective';
    if (rate >= 40) return 'Moderate';
    if (rate >= 20) return 'Poor';
    return 'Very Poor';
  }

  /**
   * Get status badge class for MDBootstrap
   */
  getStatusBadgeClass(statusId: number): string {
    switch (statusId) {
      case 1: return 'badge-success'; // Active
      case 2: return 'badge-secondary'; // Inactive
      case 3: return 'badge-primary'; // Sold
      case 4: return 'badge-warning'; // Reserved/Other
      case 6: return 'badge-info'; // Special status
      default: return 'badge-light';
    }
  }

  /**
   * Get status text from status ID
   */
  getStatusText(statusId: number): string {
    switch (statusId) {
      case 1: return 'Active';
      case 2: return 'Inactive';
      case 3: return 'Sold';
      case 4: return 'Reserved';
      case 6: return 'Withdrawn';
      default: return 'Unknown';
    }
  }

  /**
   * Get health status class
   */
  getHealthStatusClass(status: string): string {
    switch (status.toLowerCase()) {
      case 'healthy': return 'text-success';
      case 'warning': return 'text-warning';
      case 'critical': return 'text-danger';
      default: return 'text-secondary';
    }
  }

  private downloadFile(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  private getDateString(): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}${month}${day}`;
  }
}
